import React, { useEffect, useMemo, useState } from 'react';
import { Box, Grid } from '@mui/material';
import { formatDate } from 'utils/string';
import PublicService from 'services/PublicService';
import ActivityCard from 'components/ActivityCard';
import AddBookmark from 'components/AddBookmark';
import SelectComponent from 'components/Select';
import IEvent from 'interfaces/IEvent';
import { Container, FiltersContainer, NoEventsText } from './styles';
import { useLoading } from 'hooks/useLoading';
import SubTitle from 'common/SubTitle/SubTitle';

const MONTH_NAMES = [
  'Janeiro',
  'Fevereiro',
  'Março',
  'Abril',
  'Maio',
  'Junho',
  'Julho',
  'Agosto',
  'Setembro',
  'Outubro',
  'Novembro',
  'Dezembro',
];

const getEventYearAndMonth = (startDate: string) => {
  if (!startDate) return null;
  const date = new Date(startDate);
  if (isNaN(date.getTime())) return null;
  return {
    year: date.getUTCFullYear(),
    month: date.getUTCMonth(),
  };
};

const EventsList = (): JSX.Element => {
  const { openLoading, closeLoading } = useLoading();
  const [eventsList, setEventsList] = useState<IEvent[]>([]);
  const [selectedMonth, setSelectedMonth] = useState<string[]>([String(new Date().getMonth())]);
  const [selectedYear, setSelectedYear] = useState<string[]>([String(new Date().getFullYear())]);

  const service = new PublicService();

  useEffect(() => {
    getEvents();
  }, []);

  const getEvents = async () => {
    try {
      openLoading();
      const data = await service.getAllEvents();
      setEventsList(data || []);
      closeLoading();
    } catch (error) {
      closeLoading();
    }
  };

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const yearsSet = new Set<number>([currentYear, currentYear + 1]);

    eventsList.forEach(event => {
      const parsed = getEventYearAndMonth(event.startDate || '');
      if (parsed) {
        yearsSet.add(parsed.year);
      }
    });

    return Array.from(yearsSet).sort((a, b) => b - a);
  }, [eventsList]);

  const monthOptions = useMemo(
    () => [
      { label: 'Todos os meses', value: 'all' },
      ...MONTH_NAMES.map((name, index) => ({
        label: name,
        value: String(index),
      })),
    ],
    []
  );

  const yearOptions = useMemo(
    () => [
      { label: 'Todos os anos', value: 'all' },
      ...availableYears.map(year => ({
        label: String(year),
        value: String(year),
      })),
    ],
    [availableYears]
  );

  const filteredEvents = useMemo(() => {
    const monthFilter = selectedMonth[0] || 'all';
    const yearFilter = selectedYear[0] || 'all';

    return eventsList
      .filter(event => {
        const parsed = getEventYearAndMonth(event.startDate || '');
        if (!parsed) return false;

        const matchesMonth = monthFilter === 'all' || parsed.month === Number(monthFilter);
        const matchesYear = yearFilter === 'all' || parsed.year === Number(yearFilter);

        return matchesMonth && matchesYear;
      })
      .sort((a, b) => new Date(b.startDate || 0).getTime() - new Date(a.startDate || 0).getTime());
  }, [eventsList, selectedMonth, selectedYear]);

  return (
    <Container>
      <SubTitle text="Agenda" />
      <FiltersContainer>
        <div>
          <SelectComponent
            label="Mês"
            options={monthOptions}
            onChange={value => {
              const option = Array.isArray(value) ? value : [value];
              setSelectedMonth(option);
            }}
            selected={selectedMonth}
          />
        </div>
        <div>
          <SelectComponent
            label="Ano"
            options={yearOptions}
            onChange={value => {
              const option = Array.isArray(value) ? value : [value];
              setSelectedYear(option);
            }}
            selected={selectedYear}
          />
        </div>
      </FiltersContainer>
      <Box sx={{ flexGrow: 1 }}>
        {filteredEvents.length > 0 ? (
          <Grid container spacing={3.7}>
            {filteredEvents.map((event, index) => (
              <Grid key={event.id || index} xs={12} sm={6} md={4} lg={3} xl={3} item>
                <ActivityCard
                  image={event.image}
                  category={event.category}
                  date={formatDate(event.startDate || '')}
                  title={event.title}
                  description={event.description}
                  link={`/events/${event.slug}`}
                  action={<AddBookmark type="event" id={event.id} />}
                />
              </Grid>
            ))}
          </Grid>
        ) : (
          <NoEventsText>Sem eventos cadastrados</NoEventsText>
        )}
      </Box>
    </Container>
  );
};

export default EventsList;
